/**
 * Enhanced error handling utilities for ProfileEditForm
 * Provides better UX for different types of errors and recovery strategies
 */

export interface ErrorResponse {
  status: string;
  message: string;
  errors?: any;
  field_errors?: { [key: string]: string[] };
  error_count?: number;
  error_id?: string;
  timestamp?: string;
}

export interface NetworkError {
  type: 'network';
  message: string;
  isRetryable: boolean;
  originalError?: any;
}

export interface ValidationError {
  type: 'validation';
  message: string;
  fieldErrors: { [key: string]: string | null };
  errorCount: number;
}

export interface ServerError {
  type: 'server';
  message: string;
  errorId?: string;
  isRetryable: boolean;
}

export type ProcessedError = NetworkError | ValidationError | ServerError;

/**
 * Process different types of errors from profile update requests
 */
export function processProfileError(error: any): ProcessedError {
  // Network/Connection errors
  if (!error.response) {
    return {
      type: 'network',
      message: 'Unable to connect to the server. Please check your internet connection and try again.',
      isRetryable: true,
      originalError: error
    };
  }

  const { status, data } = error.response;

  // Validation errors (422)
  if (status === 422) {
    return {
      type: 'validation',
      message: data.message || 'Please fix the validation errors below.',
      fieldErrors: processValidationErrors(data.errors || data.field_errors || data),
      errorCount: data.error_count || 0
    };
  }

  // Server errors (500, 503, etc.)
  if (status >= 500) {
    return {
      type: 'server',
      message: data.message || 'A server error occurred. Please try again in a few moments.',
      errorId: data.error_id,
      isRetryable: true
    };
  }

  // Client errors (400, 401, 403, 404, etc.)
  if (status >= 400 && status < 500) {
    return {
      type: 'server',
      message: data.message || 'An error occurred while processing your request.',
      errorId: data.error_id,
      isRetryable: false
    };
  }

  // Unknown error
  return {
    type: 'server',
    message: 'An unexpected error occurred. Please try again.',
    isRetryable: true
  };
}

/**
 * Convert Rails validation errors to frontend validation error format
 */
export function processValidationErrors(railsErrors: any): { [key: string]: string | null } {
  const validationErrors: { [key: string]: string | null } = {};

  // Handle different Rails error response formats
  if (railsErrors && typeof railsErrors === 'object') {
    Object.keys(railsErrors).forEach(field => {
      const errors = railsErrors[field];
      
      if (Array.isArray(errors) && errors.length > 0) {
        // Take the first error message for each field
        validationErrors[field] = errors[0];
      } else if (typeof errors === 'string') {
        validationErrors[field] = errors;
      }
    });
  }

  return validationErrors;
}

/**
 * Generate user-friendly error messages for common validation scenarios
 */
export function getFieldErrorMessage(field: string, error: string | string[] | any): string {
  const fieldDisplayNames: { [key: string]: string } = {
    first_name: 'First name',
    last_name: 'Last name',
    phone: 'Phone number',
    zip: 'ZIP code',
    street_address: 'Street address',
    city: 'City',
    state: 'State',
    hourly_rate: 'Hourly rate',
    maximum_traveling_distance: 'Maximum travel distance',
    position_type_ids: 'Hiring availability',
    job_type_ids: 'Position availability',
    // Nested field mappings
    'experiences.employer': 'Experience employer',
    'experiences.title': 'Experience title',
    'experiences.start_date': 'Experience start date',
    'experiences.end_date': 'Experience end date',
    'educations.school_name': 'Education school name',
    'educations.qualification': 'Education qualification',
    'educations.start_date': 'Education start date',
    'educations.end_date': 'Education end date'
  };

  // Handle nested field names (e.g., "experiences.start_date", "experiences[0].start_date")
  let displayName = fieldDisplayNames[field];
  
  if (!displayName) {
    // Parse nested field names
    if (field.includes('experiences')) {
      const match = field.match(/experiences(?:\[\d+\])?\.(\w+)/);
      if (match) {
        const fieldName = match[1];
        displayName = fieldDisplayNames[`experiences.${fieldName}`] || `Experience ${fieldName.replace(/_/g, ' ')}`;
      }
    } else if (field.includes('educations')) {
      const match = field.match(/educations(?:\[\d+\])?\.(\w+)/);
      if (match) {
        const fieldName = match[1];
        displayName = fieldDisplayNames[`educations.${fieldName}`] || `Education ${fieldName.replace(/_/g, ' ')}`;
      }
    }
    
    // Fallback to formatted field name
    if (!displayName) {
      displayName = field.replace(/_/g, ' ');
    }
  }
  
  // Normalize error to string - handle arrays, objects, etc.
  let errorMessage: string;
  if (Array.isArray(error)) {
    errorMessage = error.length > 0 ? error[0].toString() : '';
  } else if (typeof error === 'string') {
    errorMessage = error;
  } else if (error && typeof error === 'object') {
    errorMessage = error.toString();
  } else {
    errorMessage = String(error || '');
  }
  
  // If we still don't have a valid error message, return a default
  if (!errorMessage) {
    return `${displayName} has an error.`;
  }
  
  // Common error message patterns
  if (errorMessage.includes('blank') || errorMessage.includes('required')) {
    return `${displayName} is required.`;
  }
  
  if (errorMessage.includes('invalid')) {
    return `Please enter a valid ${displayName.toLowerCase()}.`;
  }
  
  if (errorMessage.includes('too short')) {
    return `${displayName} is too short.`;
  }
  
  if (errorMessage.includes('too long')) {
    return `${displayName} is too long.`;
  }
  
  if (errorMessage.includes('greater than')) {
    return `${displayName} must be greater than the minimum value.`;
  }
  
  if (errorMessage.includes('less than')) {
    return `${displayName} must be less than the maximum value.`;
  }
  
  // Return the error message with field name if no pattern matches
  return `${displayName}: ${errorMessage}`;
}

/**
 * Generate error summary for display at top of form
 */
export function generateErrorSummary(errors: { [key: string]: string | null }): string[] {
  const errorList: string[] = [];
  
  Object.entries(errors).forEach(([field, error]) => {
    if (error) {
      errorList.push(getFieldErrorMessage(field, error));
    }
  });
  
  return errorList;
}

/**
 * Determine if an error is retryable and suggest next action
 */
export function getErrorAction(error: ProcessedError): {
  canRetry: boolean;
  retryDelay?: number;
  action: string;
  buttonText: string;
} {
  switch (error.type) {
    case 'network':
      return {
        canRetry: true,
        retryDelay: 2000,
        action: 'Check your internet connection and try again.',
        buttonText: 'Retry'
      };
      
    case 'validation':
      return {
        canRetry: false,
        action: 'Please fix the validation errors and try again.',
        buttonText: 'Fix Errors'
      };
      
    case 'server':
      if (error.isRetryable) {
        return {
          canRetry: true,
          retryDelay: 5000,
          action: 'A server error occurred. Please try again in a moment.',
          buttonText: 'Retry'
        };
      } else {
        return {
          canRetry: false,
          action: 'Please contact support if this problem continues.',
          buttonText: 'Contact Support'
        };
      }
      
    default:
      return {
        canRetry: true,
        retryDelay: 3000,
        action: 'Please try again.',
        buttonText: 'Retry'
      };
  }
}

/**
 * Log errors to console with structured data for debugging
 */
export function logError(error: ProcessedError, context: any = {}) {
  const logData = {
    type: error.type,
    message: error.message,
    timestamp: new Date().toISOString(),
    context,
    ...error
  };
  
  console.error('ProfileEditForm Error:', logData);
  
  // In production, you might want to send this to a logging service
  if (process.env.NODE_ENV === 'production') {
    // Example: Send to analytics or logging service
    // analytics.track('Profile Edit Error', logData);
  }
}
